
import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Insight, generateFallbackInsights } from "@/utils/insights-utils";
import { Industry, OrganizationSize } from "@/types/assessment";
import { fetchExistingInsights, checkAssessmentExists, generateLLMInsights } from "@/services/insightsService";

interface UseInsightsProps {
  assessmentId: string;
  assessmentData: {
    organizationDetails: {
      industry: Industry;
      size: OrganizationSize;
    };
    dimensionScores: Record<string, number>;
  };
}

export const useInsights = ({ assessmentId, assessmentData }: UseInsightsProps) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const toastShownRef = useRef(false);

  const generateInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For PDF export or pending assessment, return fallback insights
      if (assessmentId === 'pdf-export' || assessmentId === 'pending') {
        setInsights(generateFallbackInsights(assessmentData));
        setLoading(false);
        return;
      }

      // Only proceed with Supabase query if we have a valid UUID
      if (assessmentId) {
        // Check if assessment exists in main assessments table
        const assessmentExists = await checkAssessmentExists(assessmentId);
        
        if (!assessmentExists) {
          console.warn("Assessment not found in database. Using fallback insights.");
          setInsights(generateFallbackInsights(assessmentData));
          throw new Error("Assessment not found in database. Using offline insights.");
        }

        // Try to fetch existing insights
        const existingInsights = await fetchExistingInsights(assessmentId);

        if (existingInsights && existingInsights.length > 0) {
          setInsights(existingInsights);
          setLoading(false);
          return;
        }

        try {
          // Generate new LLM insights
          const llmInsights = await generateLLMInsights(assessmentId, assessmentData);
          setInsights(llmInsights);
        } catch (functionError: any) {
          console.error('Edge function error:', functionError);
          
          // Check for foreign key constraint error
          if (functionError.message && functionError.message.includes('foreign key constraint')) {
            setError('Database constraint error: The assessment insights could not be saved because the assessment record was not found.');
          } else {
            setError(`Failed to generate insights: ${functionError.message}`);
          }
          
          setInsights(generateFallbackInsights(assessmentData));
          
          showFallbackToast();
        }
      } else {
        // If no valid assessmentId, use fallback insights
        setInsights(generateFallbackInsights(assessmentData));
      }
    } catch (err: any) {
      console.error('Error in insights generation:', err);
      setError(err.message || 'Failed to generate insights');
      setInsights(generateFallbackInsights(assessmentData));
      
      showFallbackToast();
    } finally {
      setLoading(false);
    }
  }, [assessmentId, assessmentData, toast]);

  const showFallbackToast = () => {
    if (!toastShownRef.current) {
      toast({
        title: "Using offline insights",
        description: "We're currently unable to generate real-time insights. Showing pre-generated recommendations instead.",
        duration: 5000,
      });
      toastShownRef.current = true;
    }
  };

  useEffect(() => {
    generateInsights();

    return () => {
      toastShownRef.current = false;
    };
  }, [generateInsights]);

  const refreshInsights = () => {
    toastShownRef.current = false;
    generateInsights();
  };

  return { insights, loading, error, refreshInsights };
};
